import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useAppSelector } from "../../redux";
import { Commet } from "react-loading-indicators";

// set the dimensions and margins of the graph
const margin = { top: 70, right: 10, bottom: 70, left: 10 };

const Groupedchart = ({ width, height }) => {
  // Merge function to merge datasets based on building names
  const mergeDatasets = (...datasets) => {
    const mergedArray = [];

    datasets.forEach((data, index) => {
      data.data.forEach((item) => {
        const meterLabel = item.label;
        const mergedItem = {
          label: meterLabel,
          ...item,
          meterType: index === 0 ? "electric" : index === 1 ? "water" : "steam",
        };
        mergedArray.push(mergedItem);
      });
    });

    return mergedArray;
  };

  let graphData = mergeDatasets(
    useAppSelector((state) => state.electric),
    useAppSelector((state) => state.water),
    useAppSelector((state) => state.steam)
  );

  // one tonrefh = 3.51685 kW
  graphData = graphData.map((data) => {
    if (data.meterType === "water") {
      return {
        ...data,
        curValue: data.curValue * 3.51685 * 0.000001, // scaled down by 1mm to see other values
      };
    }
    return data;
  });

  // one lb/h = 0.30479 kW
  graphData = graphData.map((data) => {
    if (data.meterType === "steam") {
      return {
        ...data,
        curValue: data.curValue * 0.30479,
      };
    }
    return data;
  });

  const ref = useRef();
  const parseTime = d3.timeParse("%Y-%m-%dT%H:%M");

  // Specify the chart’s dimensions.
  useEffect(() => {
    if (graphData.length === 0) {
      const retryTimeout = setTimeout(() => {
        // Call the useEffect again after 10 seconds
        clearTimeout(retryTimeout);
      }, 10000); // 10 seconds

      return; // Exit early without rendering the chart
    }
    console.log(graphData);

    // Clear old data
    d3.select(ref.current).selectAll("*").remove();

    // Prepare the scales for positional and color encodings.
    // Fx encodes the building
    // Sort building names alphabetically for the domain
    const sortedNames = [...new Set(graphData.map((d) => d.name))].sort();
    const fx = d3
      .scaleBand()
      .domain(sortedNames)
      .rangeRound([margin.left, width - margin.right])
      .paddingInner(0.1);

    // Both x and color encode the meter type
    const meters = new Set(graphData.map((d) => d.meterType));

    const x = d3
      .scaleBand()
      .domain(meters)
      .rangeRound([0, fx.bandwidth()])
      .padding(0.05);

    const color = d3
      .scaleOrdinal()
      .domain(meters)
      .range(["#FFAB2A", "#6285FF", "#B5E175"])
      .unknown("#ccc");

    // Y encodes the height of the bar.
    const y = d3
      .scaleLinear()
      .domain([0, d3.max(graphData, (d) => d.curValue)])
      .nice()
      .rangeRound([height - margin.bottom, 0]);

    // Create the SVG container.
    const svg = d3
      .select(ref.current)
      .attr("width", width)
      // .attr("height", height + 100)
      .attr("height", height)
      .attr("viewBox", [
        -margin.left,
        -margin.top,
        width + margin.left + margin.right,
        height + margin.top + margin.bottom,
      ])
      .attr("style", "max-width: 100%; height: auto;");

    // Append a group for each state, and a rect for each age.
    svg
      .append("g")
      .selectAll()
      .data(d3.group(graphData, (d) => d.name))
      .join("g")
      .attr("transform", ([name]) => `translate(${fx(name)},0)`)
      .selectAll()
      .data(([, d]) => d)
      .join("rect")
      .attr("x", (d) => x(d.meterType))
      .attr("y", (d) => y(d.curValue))
      .attr("width", x.bandwidth() - 5)
      .attr("height", (d) => y(0) - y(d.curValue))
      .attr("fill", (d) => color(d.meterType))
      .attr("rx", 5)
      .attr("ry", 5);

    // Append the horizontal axis.
    const xAxis = d3
      .axisBottom(fx)
      .tickSizeOuter(0)
      .tickFormat((d) => {
        switch (d) {
          case "Baker, Berry, Carson Library - Complex":
            return "Baker-Berry Library";
          case "Rockefeller Ctr for the Social Sciences":
            return "Rockefeller Ctr";
          case "Class of 1953 Commons":
            return "1953 Commons";
          case "Class of 1978 Life Sciences Center":
            return "Life Sciences Center";
          case "Engineer and Comp Science Ctr":
            return "Engineering & Computer Science Center";
          case "Burke Chemistry Laboratory":
            return "Burke Lab";
          default:
            return d;
        }
      });

    svg
      .append("g")
      .style("stroke-width", "1px")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(xAxis)
      .style("font-size", "12px")
      .style("font-family", "National2")
      .selectAll(".tick text")
      .call(wrap, fx.bandwidth());

    // yAxis with thicker, grey line
    const yAxis = d3.axisLeft(y).ticks(5).tickFormat(d3.format(".2s"));
    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(yAxis)
      .style("font-family", "National2")
      .style("font-size", "15px")
      .call((g) => g.selectAll("text").attr("fill", "grey"))

      .call((g) =>
        g
          .append("text")
          .attr("x", -margin.left + 50)
          .attr("y", -20)
          .attr("fill", "black")
          .attr("text-anchor", "start")
          .text("Steam Consumption (kW)")
          .style("font-family", "National2")
          .style("font-size", "15px")
      )
      .selectAll("path, line")
      .style("stroke", "#888888")
      .style("stroke-width", "1px");

    function wrap(text, width) {
      text.each(function () {
        var text = d3.select(this),
          words = text.text().split(/\s+/).reverse(),
          word,
          line = [],
          lineNumber = 0,
          lineHeight = 1.1,
          y = text.attr("y"),
          dy = parseFloat(text.attr("dy") || 0),
          tspan = text
            .text(null)
            .append("tspan")
            .attr("x", 0)
            .attr("y", y)
            .attr("dy", dy + "em");
        while ((word = words.pop())) {
          line.push(word);
          tspan.text(line.join(" "));
          if (tspan.node().getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(" "));
            line = [word];
            tspan = text
              .append("tspan")
              .attr("x", 0)
              .attr("y", y)
              .attr("dy", ++lineNumber * lineHeight + dy + "em")
              .text(word);
          }
        }
      });
    }

    // Legend beneath the graph
    const legend = svg
      .append("g")
      .attr("transform", `translate(350,${height + 40})`);

    let offsetX = 0;

    meters.forEach((meter, index) => {
      const legendItem = legend
        .append("g")
        .attr("transform", `translate(${offsetX}, 0)`);

      legendItem
        .append("circle")
        .attr("cx", 10)
        .attr("cy", 8)
        .attr("r", 10)
        .attr("fill", color(meter));

      const text = legendItem
        .append("text")
        .attr("x", 30)
        .attr("y", 15)
        .text(meter.charAt(0).toUpperCase() + meter.slice(1))
        .style("font-size", "20px")
        .style("font-family", "National2");

      // Measure the text width and calculate the offset for the next item
      const textWidth = text.node().getComputedTextLength();
      offsetX += textWidth + 50;
    });

    // Append text for last updated timestamp
    // svg
    //   .append("text")
    //   .attr("class", "last-updated")
    //   .attr("x", width - 50)
    //   .attr("y", height + 50)
    //   .attr("text-anchor", "end")
    //   .attr("font-size", "14px")
    //   .style("font-style", "italic")
    //   .attr("font-family", "National2")
    //   .text("Last Updated: " + formatTime(lastUpdated));
  }, [graphData, height, width]);

  return graphData.length === 0 ? (
    <Commet color="#32cd32" size="medium" text="" textColor="" />
  ) : (
    <svg
      id="groupedchart"
      ref={ref}
      viewBox={`0 0 ${width} ${height}`}
      style={{
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        overflow: "visible",
        font: "10px National2",
      }}
    />
  );
};

export default Groupedchart;
